/* eslint-disable max-len */
import { Box } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import BasicHeader from '/@/components/basic-header';
import Container from '/@/components/container';
import Drawing from '/@/components/drawing';
import LibraryCard from '/@/components/library-card';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { axFetch } from '/@/store/session/actions';
import { getAppInstance, getUser } from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking/actions';
import { formatDate, getOS, isPhone, sanitize } from '/@/utils';
import ToggleGroup from '/@/v3/components/forms/toggle-group';
import Title from '/@/v3/components/title';
import styles from './styles.module.css';

/**
 * @typedef {{}} Props
 * @param {Props} props
 */
export default function LibraryList() {
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [filteredItems, setFilteredItems] = useState(items);
    const [categories, setCategories] = useState([]);
    const appInstance = useSelector(getAppInstance);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await getLibraryItems();
                setItems(data);
                const categoriesData = await getCategories(data);
                setCategories(categoriesData);
                setLoading(false);
            } catch (error) {
                console.error(error);
            }
        };

        fetchData();
    }, []);

    const getLibraryItems = async () => {
        try {
            const apiUrl = new URL(
                `${settings.urls.apiDomain}/core/v1/library/items/?app_instance=${appInstance.id}`,
                window.location.origin
            );

            const res = await axFetch.get(apiUrl.toString());
            return res.data;
        } catch (err) {
            console.log(err);
            return [];
        }
    };

    const getCategories = async (libraryItems) => {
        try {
            const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/library/categories/`, window.location.origin);

            const res = await axFetch.get(apiUrl.toString());
            const allCategories = res.data;

            const assignedCategorysCodenames = libraryItems.reduce((list, { categories }) => {
                if (Array.isArray(categories)) {
                    return list.concat(categories.map(({ codename }) => codename));
                }
                return list;
            }, []);

            const filteredCategories = allCategories
                .filter(({ codename }) => {
                    return assignedCategorysCodenames.includes(codename);
                })
                .sort((a, b) => a.order - b.order)
                .map(({ codename, name }) => ({ value: codename, label: _(name) }));
            return filteredCategories;
        } catch (err) {
            console.log(err);
            return [];
        }
    };

    const isIos = getOS() === 'iOS';
    const user = useSelector((state) => getUser(state));
    const offline = useSelector((state) => state.network.offline);
    /** @type {{value: number, label: string}[]} */
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const dispatch = useDispatch();
    const boundActionCreators = useMemo(() => bindActionCreators({ trackEvent }, dispatch), [dispatch]);

    /** @param {Array<number>} categoriesIds */
    const handleChangeFilter = (categoriesIds) => {
        setSelectedCategories(categoriesIds);
        const selectedCategories = categories
            .filter(({ value }) => categoriesIds.some((codename) => codename === value))
            .map(({ label }) => label);
        boundActionCreators.trackEvent({ key: 'library.list.filters', categories: selectedCategories });
    };

    useEffect(() => {
        const sanitizedSearch = sanitize(search).toLowerCase();
        let newFilteredItems = items;

        if (sanitizedSearch) {
            newFilteredItems = items.filter((item) => {
                return (
                    sanitize(_(item.name).toLowerCase()).includes(sanitizedSearch) ||
                    sanitize(item.description?.toLowerCase()).includes(sanitizedSearch) ||
                    formatDate(new Date(item.updated)).includes(sanitizedSearch)
                );
            });
        }

        if (selectedCategories.length) {
            newFilteredItems = newFilteredItems.filter((item) => {
                return item.categories.some((c) => {
                    return selectedCategories.some((value) => {
                        return c.codename === value;
                    });
                });
            });
        }

        setFilteredItems(newFilteredItems);
    }, [search, items, selectedCategories]);

    const filters = (
        <div className={styles.filters}>
            <Title theme={settings.app.theme} size='lg' className={styles.title}>
                {_('Categoría')}
            </Title>
            <ToggleGroup
                type='checkbox'
                theme={settings.app.theme}
                options={categories}
                value={selectedCategories}
                onChange={handleChangeFilter}
                flexDirection={isPhone() ? 'column' : 'row'}
                flexWrap='wrap'
            />
        </div>
    );

    if (offline) {
        return (
            <div className={styles.message}>
                <Drawing
                    type='offline'
                    title={_('Para ver los archivos de la biblioteca tenés que tener conexión a internet.')}
                />
            </div>
        );
    }
    return (
        <Container gradient='middle' color='pink' className={styles.wrapper} classNameRoot={styles.rootContainer}>
            <div className={styles.libraryContainer}>
                {isPhone() && (
                    <div className={classNames(styles.sidebar, { [styles.showSidebar]: showMobileFilters })}>
                        <div className={styles.overlay} onClick={() => setShowMobileFilters(false)} />
                        <div className={styles.sidebarContent}>
                            <div className={styles.close} onClick={() => setShowMobileFilters(false)}>
                                <i className='material-icons'>close</i>
                            </div>
                            {filters}
                        </div>
                    </div>
                )}
                <div className={styles.search}>
                    <BasicHeader
                        className={styles.header}
                        title={_('Biblioteca')}
                        searchPlaceholder={_('Buscar por nombre')}
                        searchText={search}
                        onChangeSearch={(query) => setSearch(query)}
                    />
                    {isPhone() && (
                        <div className={styles.toggle} onClick={() => setShowMobileFilters(true)}>
                            <i className='material-icons'>filter_list</i>
                        </div>
                    )}
                </div>
                {!isPhone() && filters}
                <div className={styles.libraryContent}>
                    <div className={styles.grid}>
                        {items.length > 0 &&
                            filteredItems.map((item) => (
                                <LibraryCard key={item.id} className={styles.cardContainer} item={item} />
                            ))}
                    </div>
                    {!filteredItems.length && !loading && (
                        <div className={styles.message}>
                            <Drawing
                                type='library-not-found'
                                title={
                                    search
                                        ? _('No se encontraron resultados para tu búsqueda.')
                                        : _('No hay archivos disponibles')
                                }
                            />
                        </div>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>{loading && <CircularProgress />}</Box>
                </div>
            </div>
        </Container>
    );
}
